/**
 * Admin Payment Monitoring Tab Component
 * 
 * C2: Admin dashboard for monitoring payment intents and payments.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Pill } from "@app/components/shared/Pill";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import {
  listPaymentIntents,
  verifyPaymentIntentNow,
  listPayments,
  type PaymentIntentListItem,
  type PaymentListItem,
} from "@app/services/adminPaymentsApi";
import { logger } from "@app/services/logger";
import "./admin-payment-monitoring.css";

const INTENTS_QUERY_KEY = ["admin", "payments", "intents"];
const PAYMENTS_QUERY_KEY = ["admin", "payments", "payments"];

export function AdminPaymentMonitoringTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"intents" | "payments">("intents");

  // Filters for intents
  const [intentFilters, setIntentFilters] = useState<{
    status?: string;
    user_id?: string;
    start_date?: string;
    end_date?: string;
  }>({});
  const [intentPage, setIntentPage] = useState(0);
  const intentLimit = 50;

  // Filters for payments
  const [paymentFilters, setPaymentFilters] = useState<{
    status?: string;
    user_id?: string;
    start_date?: string;
    end_date?: string;
  }>({});
  const [paymentPage, setPaymentPage] = useState(0);
  const paymentLimit = 50;

  const { data: intentsData, isLoading: intentsLoading } = useQuery({
    queryKey: [...INTENTS_QUERY_KEY, intentFilters, intentPage],
    queryFn: () =>
      listPaymentIntents({
        ...intentFilters,
        limit: intentLimit,
        offset: intentPage * intentLimit,
      }),
  });

  const { data: paymentsData, isLoading: paymentsLoading } = useQuery({
    queryKey: [...PAYMENTS_QUERY_KEY, paymentFilters, paymentPage],
    queryFn: () =>
      listPayments({
        ...paymentFilters,
        limit: paymentLimit,
        offset: paymentPage * paymentLimit,
      }),
  });

  const verifyMutation = useMutation({
    mutationFn: verifyPaymentIntentNow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PAYMENTS_QUERY_KEY });
    },
    onError: (error: unknown) => {
      logger.error("Failed to verify payment intent:", error instanceof Error ? error : new Error(String(error)));
      alert(
        error instanceof Error ? error.message : "Failed to verify payment intent",
      );
    },
  });

  const handleVerify = (intentId: string) => {
    if (confirm("Are you sure you want to verify this payment intent now?")) {
      verifyMutation.mutate(intentId);
    }
  };

  const getStatusPill = (status: string) => {
    const tone: "success" | "warning" | "danger" | "neutral" =
      status === "verified"
        ? "success"
        : status === "rejected" || status === "expired"
          ? "danger"
          : status === "pending_verification" || status === "awaiting_payment"
            ? "warning"
            : "neutral";
    return <Pill tone={tone}>{status}</Pill>;
  };

  return (
    <div className="admin-payment-monitoring-root">
      <SectionHeader
        title="Payment Monitoring"
        subtitle="Monitor and manage payment intents and payments."
      />

      <div className="payment-monitoring-tabs">
        <button
          className={`payment-monitoring-tab ${
            activeTab === "intents" ? "payment-monitoring-tab--active" : ""
          }`}
          onClick={() => setActiveTab("intents")}
        >
          Payment Intents ({intentsData?.total || 0})
        </button>
        <button
          className={`payment-monitoring-tab ${
            activeTab === "payments" ? "payment-monitoring-tab--active" : ""
          }`}
          onClick={() => setActiveTab("payments")}
        >
          Payments ({paymentsData?.total || 0})
        </button>
      </div>

      {activeTab === "intents" && (
        <Card title="Payment Intents">
          <div className="payment-monitoring-filters">
            <label>
              Status
              <select
                value={intentFilters.status || ""}
                onChange={(e) =>
                  setIntentFilters({
                    ...intentFilters,
                    status: e.target.value || undefined,
                  })
                }
              >
                <option value="">All</option>
                <option value="awaiting_payment">Awaiting Payment</option>
                <option value="pending_verification">Pending Verification</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </label>
            <label>
              User ID
              <input
                type="text"
                value={intentFilters.user_id || ""}
                onChange={(e) =>
                  setIntentFilters({
                    ...intentFilters,
                    user_id: e.target.value || undefined,
                  })
                }
                placeholder="Filter by user ID"
              />
            </label>
            <label>
              Start Date
              <input
                type="date"
                value={intentFilters.start_date || ""}
                onChange={(e) =>
                  setIntentFilters({
                    ...intentFilters,
                    start_date: e.target.value || undefined,
                  })
                }
              />
            </label>
            <label>
              End Date
              <input
                type="date"
                value={intentFilters.end_date || ""}
                onChange={(e) =>
                  setIntentFilters({
                    ...intentFilters,
                    end_date: e.target.value || undefined,
                  })
                }
              />
            </label>
            <Button
              variant="ghost"
              onClick={() => {
                setIntentFilters({});
                setIntentPage(0);
              }}
            >
              Clear Filters
            </Button>
          </div>

          {intentsLoading ? (
            <div className="payment-monitoring-loading">Loading intents...</div>
          ) : !intentsData || intentsData.intents.length === 0 ? (
            <div className="payment-monitoring-empty">No payment intents found.</div>
          ) : (
            <>
              <div className="payment-monitoring-table-container">
                <table className="payment-monitoring-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>User</th>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>TX Hash</th>
                      <th>Verify Attempts</th>
                      <th>Expires</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {intentsData.intents.map((intent) => (
                      <tr key={intent.id}>
                        <td className="payment-monitoring-code">
                          {intent.id.slice(0, 8)}...
                        </td>
                        <td>
                          {intent.user_email || intent.user_id.slice(0, 8)}
                        </td>
                        <td>{intent.plan_code || "-"}</td>
                        <td>${intent.amount_usd.toFixed(2)}</td>
                        <td>{intent.method}</td>
                        <td>{getStatusPill(intent.status)}</td>
                        <td className="payment-monitoring-code">
                          {intent.tx_hash ? `${intent.tx_hash.slice(0, 16)}...` : "-"}
                        </td>
                        <td>{intent.verify_attempts}</td>
                        <td>
                          {new Date(intent.expires_at).toLocaleDateString("en-US")}
                        </td>
                        <td>
                          {new Date(intent.created_at).toLocaleDateString("en-US")}
                        </td>
                        <td>
                          {(intent.status === "pending_verification" || intent.status === "rejected") && (
                            <Button
                              variant={intent.status === "rejected" ? "secondary" : "primary"}
                              size="small"
                              onClick={() => handleVerify(intent.id)}
                              disabled={verifyMutation.isPending}
                              title={
                                intent.status === "rejected"
                                  ? "Re-verify rejected payment intent"
                                  : "Verify payment intent now"
                              }
                            >
                              {intent.status === "rejected" ? "Re-verify" : "Verify Now"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="payment-monitoring-pagination">
                <Button
                  variant="ghost"
                  onClick={() => setIntentPage((p) => Math.max(0, p - 1))}
                  disabled={intentPage === 0}
                >
                  Previous
                </Button>
                <span>
                  Page {intentPage + 1} of{" "}
                  {Math.ceil((intentsData.total || 0) / intentLimit)}
                </span>
                <Button
                  variant="ghost"
                  onClick={() => setIntentPage((p) => p + 1)}
                  disabled={
                    (intentPage + 1) * intentLimit >= (intentsData.total || 0)
                  }
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </Card>
      )}

      {activeTab === "payments" && (
        <Card title="Payments">
          <div className="payment-monitoring-filters">
            <label>
              Status
              <select
                value={paymentFilters.status || ""}
                onChange={(e) =>
                  setPaymentFilters({
                    ...paymentFilters,
                    status: e.target.value || undefined,
                  })
                }
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </label>
            <label>
              User ID
              <input
                type="text"
                value={paymentFilters.user_id || ""}
                onChange={(e) =>
                  setPaymentFilters({
                    ...paymentFilters,
                    user_id: e.target.value || undefined,
                  })
                }
                placeholder="Filter by user ID"
              />
            </label>
            <label>
              Start Date
              <input
                type="date"
                value={paymentFilters.start_date || ""}
                onChange={(e) =>
                  setPaymentFilters({
                    ...paymentFilters,
                    start_date: e.target.value || undefined,
                  })
                }
              />
            </label>
            <label>
              End Date
              <input
                type="date"
                value={paymentFilters.end_date || ""}
                onChange={(e) =>
                  setPaymentFilters({
                    ...paymentFilters,
                    end_date: e.target.value || undefined,
                  })
                }
              />
            </label>
            <Button
              variant="ghost"
              onClick={() => {
                setPaymentFilters({});
                setPaymentPage(0);
              }}
            >
              Clear Filters
            </Button>
          </div>

          {paymentsLoading ? (
            <div className="payment-monitoring-loading">Loading payments...</div>
          ) : !paymentsData || paymentsData.payments.length === 0 ? (
            <div className="payment-monitoring-empty">No payments found.</div>
          ) : (
            <>
              <div className="payment-monitoring-table-container">
                <table className="payment-monitoring-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>User</th>
                      <th>Intent ID</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>TX Hash</th>
                      <th>Confirmed</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentsData.payments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="payment-monitoring-code">
                          {payment.id.slice(0, 8)}...
                        </td>
                        <td>
                          {payment.user_email || payment.user_id.slice(0, 8)}
                        </td>
                        <td className="payment-monitoring-code">
                          {payment.intent_id.slice(0, 8)}...
                        </td>
                        <td>${payment.amount_usd.toFixed(2)}</td>
                        <td>{payment.method}</td>
                        <td>{getStatusPill(payment.status)}</td>
                        <td className="payment-monitoring-code">
                          {payment.tx_hash ? `${payment.tx_hash.slice(0, 16)}...` : "-"}
                        </td>
                        <td>
                          {payment.confirmed_at
                            ? new Date(payment.confirmed_at).toLocaleDateString("en-US")
                            : "-"}
                        </td>
                        <td>
                          {new Date(payment.created_at).toLocaleDateString("en-US")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="payment-monitoring-pagination">
                <Button
                  variant="ghost"
                  onClick={() => setPaymentPage((p) => Math.max(0, p - 1))}
                  disabled={paymentPage === 0}
                >
                  Previous
                </Button>
                <span>
                  Page {paymentPage + 1} of{" "}
                  {Math.ceil((paymentsData.total || 0) / paymentLimit)}
                </span>
                <Button
                  variant="ghost"
                  onClick={() => setPaymentPage((p) => p + 1)}
                  disabled={
                    (paymentPage + 1) * paymentLimit >= (paymentsData.total || 0)
                  }
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
